import React, { useContext, useState } from 'react';
import { AppContext } from '../../App';
import Layout from '../../components/Layout';
import { authAxios } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

export default function AdminImport() {
  const { user, currentSeason, API } = useContext(AppContext);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
        toast.error('Please select an Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file || !currentSeason) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await authAxios.post(
        `${API}/seasons/${currentSeason.id}/import`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true
        }
      );

      setResult(response.data);
      toast.success('Import completed successfully!');
      setFile(null);
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // Create CSV content for a multi-sheet template
    const scoringRulesCSV = `Event,Points,Notes
Win Individual Immunity,5,Contestant wins immunity challenge
Find Hidden Immunity Idol,3,Finds a hidden immunity idol
Win Reward,2,Team wins reward challenge
Blindside Vote,4,Successfully orchestrates a blindside
Survive Tribal Council,1,Makes it through tribal council
Win Fire Making,5,Wins the fire making challenge
Jury Vote Received,2,Receives a vote from jury member
Runner Up,10,Finishes as runner-up (2nd place)
Sole Survivor,20,Wins the entire season`;

    const rostersCSV = `Contestant,Player
Andy Rueda,John Smith
Caroline Vidmar,Jane Doe
Gabe Ortis,Mike Johnson
Genevieve Mushaluk,Sarah Williams
Rachel LaMont,NONE
Sam Phalen,NONE
Sue Smey,NONE
Teeny Chirichillo,NONE`;

    const eventLogCSV = `Episode,Event,Contestant,Tribe,Points
1,Win Individual Immunity,Andy Rueda,Gata,5
1,Find Hidden Immunity Idol,Rachel LaMont,Lavo,3
2,Win Reward,Caroline Vidmar,Tuku,2`;

    // Create a text file with instructions
    const instructions = `SURVIVOR FANTASY LEAGUE - IMPORT TEMPLATE INSTRUCTIONS

This template shows the format for importing data into your Survivor Fantasy League.

IMPORTANT: Save each section below as a separate sheet in an Excel file (.xlsx)

===========================================
SHEET 1: "Scoring Rules"
===========================================
${scoringRulesCSV}

Instructions:
- Event: Name of the scoring event
- Points: Number of points awarded
- Notes: Optional description

===========================================
SHEET 2: "Rosters"
===========================================
${rostersCSV}

Instructions:
- Contestant: Full name of Survivor contestant
- Player: League player name (use "NONE" for unassigned)
- Creates contestants automatically if they don't exist

===========================================
SHEET 3: "Event Log" (Optional)
===========================================
${eventLogCSV}

Instructions:
- Episode: Episode number
- Event: Event that occurred
- Contestant: Contestant name
- Tribe: Tribe name (creates tribes automatically)
- Points: Points earned

===========================================
HOW TO CREATE YOUR EXCEL FILE:
===========================================
1. Open Excel or Google Sheets
2. Create three sheets: "Scoring Rules", "Rosters", "Event Log"
3. Copy the data from each section above into the corresponding sheet
4. Replace the example data with your own
5. Save as .xlsx file
6. Upload to Import Data page

===========================================
TIPS:
===========================================
- Sheet names must match exactly: "Scoring Rules", "Rosters", "Event Log"
- First row is always the header (don't change column names)
- Event Log sheet is optional but recommended for tribe data
- Player names will create placeholder accounts if they don't exist
- Contestant photos can be added later via Admin > Contestants`;

    // Download as text file
    const blob = new Blob([instructions], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Survivor_Import_Template_Instructions.txt';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    toast.success('Template instructions downloaded!');
  };

  if (user?.role !== 'admin') {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Denied</h2>
          <p className="text-slate-600">You need admin privileges to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto container-mobile">
        <div className="spacing-section">
          <h1 className="heading-hero text-slate-800 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Import Data
          </h1>
          <p className="text-responsive text-slate-600">Upload Excel file to import Scoring Rules and Rosters</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Upload Excel File</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {!currentSeason ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800">Please select a season before importing data.</p>
              </div>
            ) : (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-blue-900">Import Instructions</h3>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={downloadTemplate}
                      className="text-blue-700 border-blue-300 hover:bg-blue-100"
                    >
                      📥 Download Template
                    </Button>
                  </div>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>The Excel file should contain sheets named "Scoring Rules" and "Rosters"</li>
                    <li>"Scoring Rules" sheet: columns for Event, Points, Notes</li>
                    <li>"Rosters" sheet: columns for Contestant, Player</li>
                    <li>"Event Log" sheet (optional): will import tribe information</li>
                    <li>Click "Download Template" for a detailed example with sample data</li>
                  </ul>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Select Excel File
                  </label>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-slate-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-lg file:border-0
                      file:text-sm file:font-semibold
                      file:bg-amber-50 file:text-amber-700
                      hover:file:bg-amber-100
                      cursor-pointer"
                  />
                  {file && (
                    <p className="mt-2 text-sm text-slate-600">
                      Selected: {file.name}
                    </p>
                  )}
                </div>

                <Button
                  onClick={handleUpload}
                  disabled={!file || uploading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Uploading...
                    </>
                  ) : (
                    'Upload and Import'
                  )}
                </Button>

                {result && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <h3 className="font-semibold text-emerald-900 mb-3">Import Results</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-600">Scoring Rules</p>
                        <p className="text-2xl font-bold text-emerald-700">{result.scoring_rules}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Contestants</p>
                        <p className="text-2xl font-bold text-emerald-700">{result.contestants}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Tribes</p>
                        <p className="text-2xl font-bold text-emerald-700">{result.tribes}</p>
                      </div>
                      <div>
                        <p className="text-slate-600">Rosters</p>
                        <p className="text-2xl font-bold text-emerald-700">{result.rosters}</p>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 mt-4">
                      Import completed for Season {currentSeason.number}
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
